import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

// Register required Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const BarChart = ({ chartData }) => {
  // Format month string to display only month name
  const formatMonth = (monthStr) => {
    const date = new Date(monthStr);
    return date.toLocaleString('default', { month: 'short' });
  };

  // Process chart data from the provided statistics
  const processChartData = () => {
    if (!chartData || !chartData.monthlyStatus || chartData.monthlyStatus.length === 0) {
      return {
        labels: [],
        datasets: [],
        totals: { paid_amount: 0, overdue_amount: 0, total: 0 }
      };
    }

    const monthlyData = chartData.monthlyStatus;
    
    // Calculate totals
    const totals = monthlyData.reduce((acc, month) => {
      acc.paid_amount += month.paid_amount || 0;
      acc.overdue_amount += month.overdue_amount || 0;
      acc.total += acc.paid_amount + acc.overdue_amount|| 0;
      return acc;
    }, { paid_amount: 0, overdue_amount: 0, total: 0 });

    return {
      labels: monthlyData.map(item => formatMonth(item.month)),
      datasets: [
        {
          label: 'Paid',
          data: monthlyData.map(item => item.paid_amount || 0),
          backgroundColor: '#3DB77E',
          borderRadius: 8,
          barThickness: 30,
        },
        {
          label: 'Overdue',
          data: monthlyData.map(item => item.overdue_amount || 0),
          backgroundColor: '#F97066',
          borderRadius: 8,
          barThickness: 30,
        },
      ],
      totals
    };
  };

  const { labels, datasets, totals } = processChartData();

  // Configuration options for the chart
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => `$${tooltipItem.raw}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 0,
        },
        categoryPercentage: 0.7,
        barPercentage: 0.6,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`,
        },
      },
    },
  };

  // Data object for the chart
  const data = {
    labels,
    datasets,
  };

  // Render the chart with a custom container
  return (
    <div style={{ width: '100%', height: '400px', margin: '0 auto', paddingTop: '60px', position: 'relative' }}>
      {/* Title on the left side */}
      <div style={{ 
        position: 'absolute', 
        top: '0', 
        left: '0', 
      }}>
        <span className='text-gray-600 font-medium text-xs'>Total Revenue</span> 
        <div className='text-gray-600 font-semibold text-sm'>${totals.total.toLocaleString()}</div> 
      </div>

      {/* Custom Legend on the right side */}
      <div style={{ 
        position: 'absolute', 
        top: '0', 
        right: '0', 
        display: 'flex', 
        gap: '30px',
        alignItems: 'center',
      }}>
        <div className='flex gap-3'>
          <div className='mt-2' style={{ width: '8px', height: '8px', backgroundColor: '#3DB77E', borderRadius: '50%' }}></div>
          <div>
            <span className='text-gray-600 font-medium text-xs'>Paid</span>
            <div className='text-gray-600 font-semibold text-sm'>${totals.paid_amount.toLocaleString()}</div> 
          </div>
        </div>
        <div className='flex gap-3'>
          <div className='mt-2' style={{ width: '8px', height: '8px', backgroundColor: '#F97066', borderRadius: '50%' }}></div>
          <div>
            <span className='text-gray-600 font-medium text-xs'>Overdue</span>
            <div className='text-gray-600 font-semibold text-sm'>${totals.overdue_amount.toLocaleString()}</div> 
          </div>
        </div>
      </div>

      {/* Chart */}
      <Bar data={data} options={options} />
    </div>
  );
};

export default BarChart;

// Usage example
// <BarChart chartData={reportData[0]} />